import React from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { Package, ChevronRight } from 'lucide-react';
import { useLang } from '../contexts/LanguageContext';

export default function AccountOrdersPage() {
  const { orders, loading } = useOutletContext();
  const { lang, t } = useLang();

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="h-24 bg-[#E5E0D8] animate-pulse" />
        ))}
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <div className="text-center py-16">
        <Package className="w-16 h-16 mx-auto text-[#E5E0D8] mb-4" />
        <h2 className="text-xl font-medium mb-2">{t({ it: 'Nessun ordine', en: 'No orders' })}</h2>
        <p className="text-[#57534E] mb-6">
          {t({ it: 'Non hai ancora effettuato ordini', en: "You haven't placed any orders yet" })}
        </p>
        <Link
          to="/shop"
          data-testid="start-shopping-btn"
          className="btn-primary inline-block"
        >
          {t({ it: 'Inizia lo Shopping', en: 'Start Shopping' })}
        </Link>
      </div>
    );
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'delivered': return 'bg-[#4A5D23] text-white';
      case 'shipped': return 'bg-blue-500 text-white';
      case 'processing': return 'bg-yellow-500 text-white';
      case 'confirmed': return 'bg-green-500 text-white';
      case 'cancelled': return 'bg-[#C05746] text-white';
      default: return 'bg-[#E5E0D8] text-[#57534E]';
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: { it: 'In attesa', en: 'Pending' },
      confirmed: { it: 'Confermato', en: 'Confirmed' },
      processing: { it: 'In lavorazione', en: 'Processing' },
      shipped: { it: 'Spedito', en: 'Shipped' },
      delivered: { it: 'Consegnato', en: 'Delivered' },
      cancelled: { it: 'Annullato', en: 'Cancelled' },
    };
    return labels[status] ? t(labels[status]) : status;
  };

  return (
    <div>
      <h2 className="text-2xl font-light mb-6">{t({ it: 'I Miei Ordini', en: 'My Orders' })}</h2>
      <div className="space-y-4">
        {orders.map((order, idx) => (
          <div
            key={order.order_id}
            data-testid={`order-item-${idx}`}
            className="bg-[#F2EFE9] p-6"
          >
            <div className="flex flex-wrap justify-between items-start gap-4 mb-4">
              <div>
                <p className="font-mono text-sm text-[#57534E]">{order.order_id}</p>
                <p className="font-medium mt-1">
                  {new Date(order.created_at).toLocaleDateString(lang === 'en' ? 'en-GB' : 'it-IT', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </p>
              </div>
              <div className="text-right">
                <span className={`inline-block px-3 py-1 text-sm ${getStatusColor(order.status)}`}>
                  {getStatusLabel(order.status)}
                </span>
                <p className="font-medium text-lg mt-2">€{order.total?.toFixed(2)}</p>
              </div>
            </div>
            
            <div className="flex gap-4 overflow-x-auto pb-2 mb-4">
              {order.items?.slice(0, 4).map((item, i) => (
                <div key={i} className="shrink-0">
                  <img
                    src={item.product?.images?.[0] || 'https://via.placeholder.com/60x80'}
                    alt={item.name}
                    className="w-16 h-20 object-cover bg-white"
                  />
                </div>
              ))}
              {order.items?.length > 4 && (
                <div className="shrink-0 w-16 h-20 bg-[#E5E0D8] flex items-center justify-center text-[#57534E]">
                  +{order.items.length - 4}
                </div>
              )}
            </div>

            <div className="flex justify-between items-center pt-4 border-t border-[#E5E0D8]">
              <div>
                <p className="text-sm text-[#57534E]">
                  {order.items?.length}{' '}
                  {order.items?.length === 1
                    ? t({ it: 'articolo', en: 'item' })
                    : t({ it: 'articoli', en: 'items' })}
                </p>
                {order.tracking_number && (
                  <p className="text-sm mt-1" data-testid={`order-tracking-${idx}`}>
                    <span className="text-[#78716C]">{t({ it: 'Tracking', en: 'Tracking' })}: </span>
                    {order.tracking_number.startsWith('http') ? (
                      <a href={order.tracking_number} target="_blank" rel="noopener noreferrer" className="text-[#4A5D23] underline">
                        {t({ it: 'Segui la spedizione', en: 'Track shipment' })}
                      </a>
                    ) : (
                      <span className="font-mono">{order.tracking_number}</span>
                    )}
                    {order.carrier && <span className="text-[#78716C]"> ({order.carrier})</span>}
                  </p>
                )}
              </div>
              <Link
                to={`/account/orders/${order.order_id}`}
                className="text-[#4A5D23] text-sm font-medium flex items-center gap-1 hover:underline"
              >
                {t({ it: 'Dettagli', en: 'Details' })}
                <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
